import styled, { css } from "styled-components";

import { BadgeProps } from "./Badge";
import { FontStyle } from "../FontStyle";

const DEFAULT_DIMENSION = 8;
const LARGE_FONT_FROM_DIMENSION = 18;
const CIRCLE_PADDING_INLINE = 5;
const PILL_PADDING_INLINE = 10;
const DIGIT_INLINE_SIZE = 7;
const SMALL_CIRCLE_BELOW_DIMENSION = 16;

type StyledBadgeProps = Partial<BadgeProps> & { hasLabel?: boolean };

const dimensionOf = (props: StyledBadgeProps) =>
  props.dimension ?? DEFAULT_DIMENSION;

const paddingInlineOf = (props: StyledBadgeProps) => {
  if (!props.hasLabel) {
    return 0;
  }

  if (props.shape === "pill") {
    return PILL_PADDING_INLINE;
  }

  const dimension = dimensionOf(props);

  if (dimension >= SMALL_CIRCLE_BELOW_DIMENSION) {
    return CIRCLE_PADDING_INLINE;
  }

  // A small circle keeps a single digit inside its box only with less padding.
  return Math.max(0, Math.floor((dimension - DIGIT_INLINE_SIZE) / 2));
};

export const StyledBadge = styled.div<StyledBadgeProps>`
  ${FontStyle}
  align-items: center;
  background-color: ${(props) =>
    !props.backgroundColor ? "var(--color-primary)" : props.backgroundColor};
  block-size: ${(props) => dimensionOf(props) + "px"};
  border-radius: 999px;
  box-sizing: border-box;
  color: ${(props) =>
    !props.textColor ? "var(--color-theme-100)" : props.textColor};
  display: flex;
  font-size: ${(props) =>
    dimensionOf(props) >= LARGE_FONT_FROM_DIMENSION ? "12px" : "10px"};
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  inline-size: auto;
  justify-content: center;
  min-inline-size: ${(props) => dimensionOf(props) + "px"};
  padding-inline: ${(props) => paddingInlineOf(props) + "px"};
  pointer-events: none;
  position: absolute;
  z-index: 1;

  ${({ shadows }) =>
    shadows &&
    css`
      box-shadow: var(--shadow-tertiary);
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
    `}

  ${({ isStandalone }) =>
    isStandalone &&
    css`
      position: static;
      z-index: 0;
    `}

  ${({ position }) =>
    position === "top-left" &&
    css`
      inset-block-start: 0;
      inset-inline-start: 0;
    `}

  ${({ position }) =>
    position === "top-right" &&
    css`
      inset-block-start: 0;
      inset-inline-end: 0;
    `}

    ${({ position }) =>
    position === "bottom-right" &&
    css`
      inset-block-end: 0;
      inset-inline-end: 0;
    `}

    ${({ position }) =>
    position === "bottom-left" &&
    css`
      inset-block-end: 0;
      inset-inline-start: 0;
    `}
`;
